import { demoLots, DemoError } from "./demo.js";
import { Refusal } from "./franchises.js";
import { supabase } from "./supabase.js";
import type { EventEntry, Lot, LotPlayer, PoolLots } from "./types.js";

interface WhoRow {
  display_name: string;
  avatar_url: string | null;
}

interface LotPlayerRow {
  member_id: string;
  members: WhoRow | null;
}

interface LotRow {
  id: string;
  event_id: string;
  position: number;
  name: string | null;
  base_price: number | null;
  auction_lot_players: LotPlayerRow[];
}

const LOT_COLUMNS =
  "id, event_id, position, name, base_price, auction_lot_players (member_id, members!member_id (display_name, avatar_url))";

function refuse(error: { message: string; code?: string | null }): never {
  throw new Refusal(error.message, error.code ?? null);
}

/**
 * Lots in their auction order, each with its players by name. The embed of `members` carries a
 * column hint for the reason `seeding.ts` does: a table with two ways to reach the same neighbour
 * makes PostgREST answer 300.
 */
export function toLots(rows: LotRow[]): Lot[] {
  return rows
    .map(
      (row): Lot => ({
        id: row.id,
        eventId: row.event_id,
        position: row.position,
        name: row.name,
        basePrice: row.base_price,
        players: row.auction_lot_players
          .map((p) => ({
            memberId: p.member_id,
            displayName: p.members?.display_name ?? "Someone",
            avatarUrl: p.members?.avatar_url ?? null,
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName)),
      }),
    )
    .sort((a, b) => a.position - b.position);
}

/**
 * The bidding players who are in no lot, by name from the pool's own entries. The candidates
 * are the database's word for who is bid on (registered, not a captain); a candidate with no
 * entry cannot be named, and is left out rather than shown as nobody.
 */
export function toUnassigned(
  candidateIds: readonly string[],
  lots: readonly Lot[],
  entries: readonly EventEntry[],
): LotPlayer[] {
  const placed = new Set(lots.flatMap((lot) => lot.players.map((p) => p.memberId)));
  const named = new Map(entries.map((e) => [e.memberId, e]));

  return candidateIds
    .filter((id) => !placed.has(id) && named.has(id))
    .map((id) => {
      const entry = named.get(id)!;
      return { memberId: id, displayName: entry.displayName, avatarUrl: entry.avatarUrl };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

async function fetchLots(eventId: string): Promise<Lot[]> {
  const { data, error } = await supabase!
    .from("auction_lots")
    .select(LOT_COLUMNS)
    .eq("event_id", eventId)
    .order("position", { ascending: true });

  if (error) refuse(error);
  return toLots(data as unknown as LotRow[]);
}

/**
 * A pool's lots as an organiser sees them: in order with their players, and the bidding players
 * in none. `entries` are the pool's registrations, which already carry names.
 */
export async function fetchPoolLots(
  eventId: string,
  entries: readonly EventEntry[],
): Promise<PoolLots> {
  if (!supabase) return demoLots(eventId);

  const [lots, candidates] = await Promise.all([
    fetchLots(eventId),
    supabase.from("auction_pool_candidates").select("member_id").eq("event_id", eventId),
  ]);

  if (candidates.error) refuse(candidates.error);
  const ids = (candidates.data ?? []).map((row) => row.member_id as string);

  return { lots, unassigned: toUnassigned(ids, lots, entries) };
}

/**
 * The pool's lots as a captain sees them once they are published: nothing before, and nothing
 * for anybody who is not entitled to them, because the database sends no rows.
 */
export async function fetchPublishedLots(eventId: string): Promise<Lot[]> {
  if (!supabase) return [];
  return fetchLots(eventId);
}

export async function createLot(eventId: string): Promise<string> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("create_lot", { target_event: eventId });
  if (error) refuse(error);
  return data as string;
}

export async function removeLot(lotId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("remove_lot", { target_lot: lotId });
  if (error) refuse(error);
}

/** The pool's lots in the order given, which must name every one once. */
export async function reorderLots(eventId: string, lotIds: string[]): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("reorder_lots", {
    target_event: eventId,
    lot_ids: lotIds,
  });
  if (error) refuse(error);
}

/** Name a lot, or clear its name with null or a blank, which leaves it shown by its number. */
export async function setLotName(lotId: string, name: string | null): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_lot_name", { target_lot: lotId, new_name: name });
  if (error) refuse(error);
}

/** Set a lot's base price in whole points, or clear it with null. */
export async function setLotPrice(lotId: string, price: number | null): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_lot_price", { target_lot: lotId, price });
  if (error) refuse(error);
}

/** Place a bidding player in a lot, move them to another, or take them out with a null lot. */
export async function placeLotPlayer(
  eventId: string,
  memberId: string,
  lotId: string | null,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("place_lot_player", {
    target_event: eventId,
    target_member: memberId,
    target_lot: lotId,
  });
  if (error) refuse(error);
}

export async function publishLots(eventId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("publish_lots", { target_event: eventId });
  if (error) refuse(error);
}

export async function unpublishLots(eventId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("unpublish_lots", { target_event: eventId });
  if (error) refuse(error);
}
